import { TokenType } from "@gain/common-enum";
import { HttpError } from "@gain/common-client/browser";
import { BaseStore } from "#shared/store/base-store.ts";
import { commonApi } from "#shared/api/common-api.ts";
import { redirectToLogin } from "#shared/lib/router-utils.ts";
import {
  isImpersonationToken,
  isTokenExpired,
  loadToken,
  parseJwtExp,
  parseJwtPayload,
  peekToken,
  persistToken,
  removeToken,
  removeTokens,
} from "#shared/lib/token-utils.ts";
import type { JwtPayload } from "#shared/lib/token-utils.ts";
import { getApiErrorMessage } from "#shared/lib/error-util.ts";
import { companyStorage } from "#shared/lib/company-storage.ts";
import {
  loadAdminConsoleRoute,
  removeAdminConsoleRoute,
} from "#shared/lib/admin-console-route.ts";

// 401/403: the server rejected the credentials. 450: the client had no token to
// send. Anything else (offline, timeout, 5xx, restarting API) says nothing about
// the session, so the user must keep it.
const CREDENTIAL_FAILURE_STATUSES = new Set([401, 403, 450]);

function isCredentialFailure(error: unknown): boolean {
  return (
    error instanceof HttpError && CREDENTIAL_FAILURE_STATUSES.has(error.status)
  );
}

export abstract class AuthStore extends BaseStore {
  tokenType: TokenType;
  isAuthenticated: boolean;
  isLoading = false;
  userId: string | null = null;
  userEmail: string | null = null;
  userName: string | null = null;
  userProfileUrl: string | null = null;

  private masterToken: string | null = null;
  protected contextToken: string | null = null;
  private _contextTokenPayload: JwtPayload | null = null;
  protected refreshTimer: ReturnType<typeof setTimeout> | null = null;

  get hasContextToken(): boolean {
    return this.contextToken != null;
  }

  get contextTokenPayload(): JwtPayload | null {
    return this._contextTokenPayload;
  }

  get isImpersonating(): boolean {
    return typeof this._contextTokenPayload?.impersonatedBy === "string";
  }

  constructor(tokenType: TokenType) {
    super();
    this.tokenType = tokenType;
    this.masterToken = loadToken(TokenType.Master, "global");
    this.isAuthenticated = this.masterToken != null;
    this.extractUserInfo();
  }

  private extractUserInfo(): void {
    if (!this.masterToken) return;
    const payload = parseJwtPayload(this.masterToken);
    if (!payload) return;
    this.userId = payload.userId ?? null;
    this.userEmail = payload.email ?? null;
    this.userName = payload.name ?? null;
    this.userProfileUrl = payload.profileUrl ? atob(payload.profileUrl) : null;
  }

  protected abstract getContextId(): string;

  protected async generateToken(): Promise<string> {
    throw new Error("generateToken must be implemented by subclass");
  }

  protected applyContextToken(token: string): void {
    this.contextToken = token;
    this._contextTokenPayload = parseJwtPayload(token);
  }

  protected shouldScheduleRefresh(): boolean {
    return !this.isImpersonating;
  }

  async initContext(): Promise<void> {
    this.isLoading = true;
    this.emit();
    try {
      await this.refreshToken();
    } finally {
      this.isLoading = false;
      this.emit();
    }
  }

  getToken(type: TokenType): string | null {
    const token =
      type === TokenType.Master ? this.masterToken : this.contextToken;

    if (!token || isTokenExpired(token)) {
      if (type === TokenType.Master) {
        // Master tokens cannot be refreshed — force re-authentication
        this.cleanAuth();
      } else {
        this.contextToken = null;
        this._contextTokenPayload = null;
      }
      return null;
    }

    return token;
  }

  async refreshToken(): Promise<void> {
    try {
      const contextId = this.getContextId();
      const peeked = peekToken(this.tokenType, contextId);
      if (peeked && isImpersonationToken(peeked) && isTokenExpired(peeked)) {
        removeToken(this.tokenType, contextId);
        const route = loadAdminConsoleRoute();
        if (route) window.location.href = route;
        return;
      }

      const stored = loadToken(this.tokenType, contextId);
      if (stored && !isTokenExpired(stored)) {
        this.applyContextToken(stored);
        return;
      }

      console.log("Refreshing token from server", this.tokenType);
      const nextToken = await this.generateToken();
      // Logout removes the master token while this request is in flight.
      if (!loadToken(TokenType.Master, "global")) return;
      this.applyContextToken(nextToken);
      persistToken(this.tokenType, contextId, nextToken);
    } catch (error) {
      console.error(error);
      if (isCredentialFailure(error)) {
        this.cleanAuth(getApiErrorMessage(error));
        return;
      }
      // Keep tokens: server is down / restarting, credentials are still valid.
      redirectToLogin(getApiErrorMessage(error));
    } finally {
      this.scheduleRefresh();
    }
  }

  private scheduleRefresh(): void {
    if (!this.contextToken) return;
    if (!this.shouldScheduleRefresh()) return;
    if (this.refreshTimer) clearTimeout(this.refreshTimer);

    const exp = parseJwtExp(this.contextToken);
    if (exp == null) return;

    const jitterMs = (60 + Math.random() * 60) * 1000;
    const delayMs = exp * 1000 - Date.now() - jitterMs;
    if (delayMs <= 0) return;

    const refreshAt = new Date(Date.now() + delayMs);
    console.log(
      `[auth] scheduled ${this.tokenType} token refresh in ${Math.round(delayMs / 1000)}s (at ${refreshAt.toLocaleTimeString()})`,
    );

    this.refreshTimer = setTimeout(() => {
      void this.refreshToken();
    }, delayMs);
  }

  async logout(): Promise<void> {
    try {
      await commonApi.auth.logout(
        {},
        { fetchOptions: { credentials: "include" } },
      );
    } finally {
      this.cleanAuth();
    }
  }

  cleanAuth(errorMessage?: string): void {
    this.contextToken = null;
    this._contextTokenPayload = null;
    removeTokens();
    companyStorage.remove();
    removeAdminConsoleRoute();
    redirectToLogin(errorMessage);
  }
}
